// File: noviopus-ats-client/src/features/Auth/authSlice.ts
import { IUser } from '@/types/user';
import { IAccount } from '@/types/account';
import { UserPreferences } from '@noviopusnpm/common';
import { REHYDRATE } from 'redux-persist';

// --- TYPES ---
export interface SsoUser extends IUser {
  preferences: UserPreferences;
  roles: string[];
  isBlock: boolean;
}

export interface AuthState {
  isAuth: boolean;
  authUser: SsoUser | null;
  currentAccount: IAccount | null;
  isLoading: boolean;
  isInitialCheckComplete: boolean;
}

// --- CONSTANTS ---
export const SET_AUTH_STATUS = 'app/Auth/SET_AUTH_STATUS';
export const SET_CURRENT_USER = 'app/Auth/SET_CURRENT_USER';
export const SET_CURRENT_ACCOUNT = 'app/Auth/SET_CURRENT_ACCOUNT';
export const UPDATE_CURRENT_USER = 'app/Auth/UPDATE_CURRENT_USER';
export const SET_AUTH_LOADING = 'app/Auth/SET_AUTH_LOADING';
export const SET_INITIAL_CHECK_COMPLETE = 'app/Auth/SET_INITIAL_CHECK_COMPLETE';
export const LOGOUT = 'app/Auth/LOGOUT';

// --- INITIAL STATE (Pure JS Object) ---
export const initialState: AuthState = {
  isAuth: false,
  authUser: null,
  currentAccount: null,
  isLoading: true,
  isInitialCheckComplete: false,
};

// --- ACTIONS ---
export const setAuthStatus = (status: boolean) => ({ type: SET_AUTH_STATUS, payload: status });
export const setCurrentUser = (user: SsoUser | null) => ({ type: SET_CURRENT_USER, payload: user });
export const setCurrentAccount = (account: IAccount | null) => ({ type: SET_CURRENT_ACCOUNT, payload: account });
export const updateCurrentUser = (user: Partial<SsoUser>) => ({ type: UPDATE_CURRENT_USER, payload: user });
export const setAuthLoading = (loading: boolean) => ({ type: SET_AUTH_LOADING, payload: loading });
export const setInitialCheckComplete = (complete: boolean) => ({ type: SET_INITIAL_CHECK_COMPLETE, payload: complete });
export const logout = () => ({ type: LOGOUT });

// --- REDUCER (Pure JS) ---
export default function authReducer(state = initialState, action: any): AuthState {
  switch (action.type) {
    case REHYDRATE:
      // If hydration brings back Immutable data, we ignore it or convert it.
      // For now, we prefer starting fresh or merging strictly JS.
      const incoming = action.payload && action.payload.auth;
      if (incoming) {
          // Check if incoming is Immutable (legacy persistence) and convert to JS
          const isImmutable = incoming && typeof incoming.toJS === 'function';
          return { 
              ...state, 
              ...(isImmutable ? incoming.toJS() : incoming),
              isInitialCheckComplete: false,
              isLoading: true 
          };
      }
      return { ...state, isInitialCheckComplete: false, isLoading: true };

    case SET_AUTH_STATUS:
      return { ...state, isAuth: action.payload };

    case SET_CURRENT_USER:
      const newUser = action.payload;
      
      console.log('[AuthSlice] Setting User (POJO):', newUser ? newUser.email : 'null');

      if (newUser) {
        // Avatar safeguard
        const currentAvatar = state.authUser?.avatar;
        if (currentAvatar && (!newUser.avatar || newUser.avatar === '')) {
          newUser.avatar = currentAvatar;
        }
        
        return {
            ...state,
            authUser: newUser,
            isAuth: true
        };
      }
      return {
          ...state,
          authUser: null,
          isAuth: false
      };

    case SET_CURRENT_ACCOUNT:
      return { ...state, currentAccount: action.payload };

    case UPDATE_CURRENT_USER:
      return {
          ...state,
          authUser: { ...state.authUser, ...action.payload } as SsoUser
      };

    case SET_AUTH_LOADING:
      return { ...state, isLoading: action.payload };

    case SET_INITIAL_CHECK_COMPLETE:
      return { ...state, isInitialCheckComplete: action.payload };

    case LOGOUT:
      return { ...initialState, isInitialCheckComplete: true };

    default:
      return state;
  }
}